import { useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Copy, MapPin, Truck } from "lucide-react";

import { AccountShell } from "../../components/desktop/account-nav.tsx";
import { Button, ErrorBlock, Img, OrderStatus, Price, Spinner } from "../../components/ui.tsx";
import { api } from "../../api/client.ts";

/**
 * ═══════════════════════════════════════════════════════════════
 *  网页版 · 订单详情
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **左边"这单是什么"，右边"钱和进度"。**
 *    手机版是物流 → 商品 → 金额 → 收货 → 退款 → 时间线一路往下滚六段，
 *    网页上照搬的话要滚三屏才看得完一单。这里把**要核对的**（商品、收货信息）
 *    放左边，把**要追踪的**（金额、进度、退款）收进右栏，一屏看完。
 *
 * ⚠️ 时间线是服务端按白名单裁剪过的：只出"发生了什么 + 什么时候"，
 *    不带 operator 之类的内部字段。
 */

interface OrderDetail {
  id: number;
  orderNo: string;
  payment: string;
  fulfillment: string;
  goodsAmount: number;
  freightAmount: number;
  discountAmount: number;
  payableAmount: number;
  paidAmount: number;
  refundedAmount: number;
  shipName: string;
  shipPhone: string;
  shipAddress: Record<string, string>;
  buyerNote: string;
  shippingCarrier: string | null;
  trackingNo: string | null;
  createdAt: string;
  items: {
    id: number;
    titleSnapshot: string;
    specsSnapshot: Record<string, string> | null;
    /** ⚠️ 服务端已把存储 key 转成地址，前端不用管 key 怎么拼。 */
    imageUrl: string | null;
    unitPrice: number;
    quantity: number;
  }[];
  timeline: { event: string; label: string; at: string }[];
  refunds: { refundNo: string; amount: number; status: string; reason: string; createdAt: string }[];
}

const REFUND_STATUS: Record<string, string> = {
  pending: "待审核",
  done: "已退款",
  rejected: "已驳回",
};

export function DesktopOrderDetail() {
  const { id } = useParams({ from: "/orders/$id" });
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ["order", id],
    queryFn: () => api.get<OrderDetail>(`/orders/${id}`),
  });

  if (isPending)
    return (
      <AccountShell title="订单详情" crumb="订单详情">
        <Spinner compact />
      </AccountShell>
    );
  if (error)
    return (
      <AccountShell title="订单详情" crumb="订单详情">
        <ErrorBlock error={error} onRetry={() => refetch()} />
      </AccountShell>
    );

  const addr = data.shipAddress ?? {};

  return (
    <AccountShell title="" crumb="订单详情">
      <div className="mb-5 flex items-center gap-4">
        <h1 className="text-[22px] font-medium">订单详情</h1>
        <span className="font-mono text-[13px] text-muted-foreground">{data.orderNo}</span>
        <OrderStatus payment={data.payment as never} fulfillment={data.fulfillment as never} />
        <span className="ml-auto text-[13px] text-muted-foreground">
          {new Date(data.createdAt).toLocaleString("zh-CN")} 下单
        </span>
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)_300px] items-start gap-6">
        {/* ══════════ 左：这单是什么 ══════════ */}
        <div className="space-y-5">
          {/* 🔴 这一段就是这一页存在的理由：之前单号填了但买家看不到。 */}
          {data.trackingNo && (
            <section className="rounded-xl border border-brand-200 bg-brand-50/60 p-5">
              <h2 className="mb-2.5 flex items-center gap-2 text-[15px] font-medium">
                <Truck className="size-[18px] text-brand-600" />
                物流
              </h2>
              <div className="flex items-center gap-4">
                <div className="min-w-0 flex-1">
                  <p className="text-[14px]">{data.shippingCarrier || "承运商"}</p>
                  <p className="mt-0.5 font-mono text-[14px] text-muted-foreground">
                    {data.trackingNo}
                  </p>
                </div>
                {/*
                  ⚠️ 建材配送常要把单号发给师傅或物业，复制按钮比让人手抄靠谱。
                     clipboard 在非安全上下文里不可用，失败就静默 —— 用户还能手选。
                */}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    void navigator.clipboard?.writeText(data.trackingNo!).catch(() => {});
                  }}
                >
                  <Copy className="size-3.5" />
                  复制单号
                </Button>
              </div>
            </section>
          )}

          {/* ── 商品 ── */}
          <section className="overflow-hidden rounded-xl border border-border bg-card">
            <h2 className="border-b border-border bg-secondary/40 px-5 py-2.5 text-[13px] text-muted-foreground">
              商品清单
            </h2>
            {data.items.map((it) => (
              <div
                key={it.id}
                className="grid grid-cols-[minmax(0,1fr)_110px_70px_110px] items-center gap-4 border-b border-border px-5 py-3.5 last:border-b-0"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <Img
                    src={it.imageUrl}
                    alt=""
                    className="size-14 shrink-0 rounded-lg object-cover"
                  />
                  <div className="min-w-0">
                    <p className="line-clamp-1 text-[14px]">{it.titleSnapshot}</p>
                    <p className="mt-0.5 text-[12px] text-muted-foreground">
                      {Object.values(it.specsSnapshot ?? {}).join(" · ")}
                    </p>
                  </div>
                </div>
                <span className="text-right text-[14px] text-muted-foreground">
                  <Price cents={it.unitPrice} />
                </span>
                <span className="text-center text-[14px] text-muted-foreground">
                  × {it.quantity}
                </span>
                <span className="text-right text-[14px]">
                  <Price cents={it.unitPrice * it.quantity} />
                </span>
              </div>
            ))}
          </section>

          {/* ── 收货信息 ── */}
          <section className="rounded-xl border border-border bg-card p-5">
            <h2 className="mb-2.5 flex items-center gap-2 text-[15px] font-medium">
              <MapPin className="size-[18px] text-muted-foreground" />
              收货信息
            </h2>
            <p className="text-[14px]">
              {data.shipName}
              <span className="ml-3 text-muted-foreground">{data.shipPhone}</span>
            </p>
            <p className="mt-1 text-[14px] leading-relaxed text-muted-foreground">
              {addr.province}
              {addr.city}
              {addr.district} {addr.detail}
            </p>
            {data.buyerNote && (
              <p className="mt-2 text-[13px] text-muted-foreground">备注：{data.buyerNote}</p>
            )}
          </section>

          {/* ── 退款记录 ── */}
          {data.refunds.length > 0 && (
            <section className="rounded-xl border border-border bg-card p-5">
              <h2 className="mb-3 text-[15px] font-medium">退款记录</h2>
              {data.refunds.map((r) => (
                <div
                  key={r.refundNo}
                  className="border-t border-border py-2.5 first:border-0 first:pt-0"
                >
                  <div className="flex justify-between text-[14px]">
                    <span className="font-mono text-[13px] text-muted-foreground">
                      {r.refundNo}
                    </span>
                    <span>
                      <Price cents={r.amount} /> · {REFUND_STATUS[r.status] ?? r.status}
                    </span>
                  </div>
                  {r.reason && (
                    <p className="mt-0.5 text-[13px] text-muted-foreground">{r.reason}</p>
                  )}
                </div>
              ))}
            </section>
          )}
        </div>

        {/* ══════════ 右：钱和进度 ══════════ */}
        <aside className="space-y-4">
          <section className="rounded-xl border border-border bg-card p-5">
            <h2 className="mb-3 text-[15px] font-medium">金额</h2>
            <dl className="space-y-2.5 text-[14px]">
              <div className="flex justify-between text-muted-foreground">
                <dt>商品</dt>
                <dd>
                  <Price cents={data.goodsAmount} />
                </dd>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <dt>运费</dt>
                <dd>
                  <Price cents={data.freightAmount} />
                </dd>
              </div>
              {data.discountAmount > 0 && (
                <div className="flex justify-between text-brand-600">
                  <dt>优惠</dt>
                  <dd>
                    −<Price cents={data.discountAmount} />
                  </dd>
                </div>
              )}
            </dl>
            <div className="mt-3.5 flex items-baseline justify-between border-t border-border pt-3.5">
              <span className="text-[14px] font-medium">实付</span>
              <Price cents={data.payableAmount} className="text-[22px] text-brand-600" />
            </div>
            {data.refundedAmount > 0 && (
              <p className="mt-2 flex justify-between text-[13px] text-muted-foreground">
                <span>已退款</span>
                <Price cents={data.refundedAmount} />
              </p>
            )}
          </section>

          {/* ── 时间线 ── */}
          <section className="rounded-xl border border-border bg-card p-5">
            <h2 className="mb-3.5 text-[15px] font-medium">订单进度</h2>
            {/*
              ⚠️ 网页上做成竖直的时间轴（点 + 连线）：手机上一行行列出来是因为
                 宽度不够，网页上这一栏窄而高，正好是时间轴的形状。
            */}
            <ol className="relative space-y-4 pl-4">
              <span className="absolute top-1.5 bottom-1.5 left-[3px] w-px bg-border" />
              {data.timeline.map((t, i) => (
                <li key={`${t.event}-${i}`} className="relative">
                  <span className="absolute top-1.5 -left-4 size-[7px] rounded-full bg-brand-600 ring-2 ring-card" />
                  <p className="text-[14px]">{t.label}</p>
                  <p className="mt-0.5 text-[12px] text-muted-foreground">
                    {new Date(t.at).toLocaleString("zh-CN")}
                  </p>
                </li>
              ))}
            </ol>
          </section>
        </aside>
      </div>
    </AccountShell>
  );
}
